import React from "react";
import MuiAlert from "@mui/material/Alert";
import PropTypes from "prop-types";

/**
 * Alert component wrapper for displaying important messages.
 * Wraps Material-UI Alert with severity variants.
 * 
 * @component
 * @example
 * <Alert severity="error">This is an error alert</Alert>
 * <Alert severity="success">This is a success alert</Alert>
 * 
 * @example
 * <Alert severity="warning" onClose={handleClose}>
 *   This alert can be closed
 * </Alert>
 */
const Alert = ({ children, ...props }) => {
  return <MuiAlert {...props}>{children}</MuiAlert>;
};

Alert.propTypes = {
  /**
   * The content of the component
   */
  children: PropTypes.node,
  /**
   * The severity of the alert. This defines the color and icon used
   */
  severity: PropTypes.oneOf(["error", "warning", "info", "success"]),
  /**
   * The variant to use
   */
  variant: PropTypes.oneOf(["filled", "outlined", "standard"]),
  /**
   * Callback fired when the component requests to be closed
   */
  onClose: PropTypes.func,
  /**
   * Override the icon displayed before the children
   */
  icon: PropTypes.node,
  /**
   * The ARIA role attribute of the element
   */
  role: PropTypes.string,
  /**
   * The component used for the root node
   */
  component: PropTypes.elementType,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
};

// No defaultProps - maintain exact MUI Alert behavior
// MUI Alert defaults: severity="info", variant="standard"

export default Alert;

